import * as React from 'react'
import {
  TextField,
  FormControl,
  InputLabel,
  Box,
  type SelectChangeEvent, Grid, Card, CardContent, Snackbar, Alert, CircularProgress, Stack
} from '@mui/material'
import {
  OrganizationsTypeSelect,
  CountrySelector,
  type MissingValueFilter,
  MissingValueSelectComponent,
  SimpleDialogQuery,
  type Module
  , type OrgParams, ParentOrgSelect
} from 'components'
import { type Country } from 'services'
import FormDialog from './form-dialog'
import { http } from 'interfaces'
import { ProjectModule, useSessionContext } from 'session'
import SaveIcon from '@mui/icons-material/Save'
import { ButtonWithIcon } from 'components'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FileDownloadIcon from '@mui/icons-material/FileDownload'

export interface OrgTableFiltersProps {
  filters: OrgFilters
  updateFilters: (filters: any) => void
  doFilter: () => Promise<void>
  cleanFilters: () => void
}

export interface OrgFilters {
  id?: number
  uniqueId: string
  name: string
  type: number
  parent_orgs: OrgParams[]
  missing_value: MissingValueFilter | undefined
  country: number | null
  country_name: string | null
  page: number
  order_field: string
  order_direction: 'ASC' | 'DESC'

}

export const OrganizationsFilters: React.FC<OrgTableFiltersProps> = ({ filters, updateFilters, doFilter, cleanFilters }) => {
  const [isChecked, setIsChecked] = React.useState(false)
  const [queryName, setqueryName] = React.useState('')
  const [fileName, setfileName] = React.useState('')
  const [showSuccessAlert, setShowSuccessAlert] = React.useState(false)
  const [showUnsuccessAlert, setShowUnsuccessAlert] = React.useState(false)
  const [showSuccessExportAlert, setShowSuccessExportAlert] = React.useState(false)
  const [showUnsuccessExportAlert, setShowUnsuccessExportAlert] = React.useState(false)
  const { sessionData } = useSessionContext()
  const [loading, setLoading] = React.useState(false)
  const [showAlertCountry, setShowAlertCountry] = React.useState(false)
  const missingValueFilters: Record<string, MissingValueFilter> = {
    Email: { label: 'Email', query: 'count_email', value: 0 },
    Phone: { label: 'Phone', query: 'count_phone', value: 0 },
    'Image/Logo': { label: 'Image/Logo', query: 'count_picture', value: 0 }
  }
  const module: Module = {
    id: ProjectModule.getId(sessionData.module_name) ?? 0,
    name: sessionData.module_name
  }

  function handleSnackbarClose (_event: React.SyntheticEvent | Event, reason?: string): void {
    if (reason === 'clickaway') {
      return
    }

    setShowSuccessExportAlert(false)
    setShowUnsuccessExportAlert(false)
  }

  const handleInputChange = (
    event: SelectChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    filterKey: keyof OrgFilters
  ): void => {
    const value = event.target.value
    const newFilter = { [filterKey]: value }
    updateFilters(newFilter)
  }
  const handleParentOrgChange = (
    option: OrgParams[]
  ): void => {
    const newFilter = { parent_orgs: option }
    updateFilters(newFilter)
  }

  const handleCountryChange = (country: Country | null): void => {
    const newFilter = { country: country?.id, country_name: country?.country_desc }
    updateFilters(newFilter)
  }

  const handleMissingValueChange = (
    missingValueFilter: MissingValueFilter | undefined
  ): void => {
    const newFilter = { missing_value: missingValueFilter }
    updateFilters(newFilter)
  }

  const handleFilterClick = (): void => {
    if (filters.page === 1) { doFilter().catch((reason) => { console.log(reason) }) } else { updateFilters({ page: 1 }) }
  }

  const handleFormCheckboxChange = (isChecked: boolean, queryName: string): void => {
    setIsChecked(isChecked)
    setqueryName(queryName)
  }
  const handleExportChange = (isChecked: boolean, queryName: string): void => {
    setfileName(queryName)
  }

  const handleFormExportChange = (): void => {
    if (filters.country == null) {
      setShowAlertCountry(true)
      return
    }
    setShowAlertCountry(false)
    setLoading(true)
    const columns = ['phone', 'address', 'url']
    const params: any = {
      columns,
      id: filters.id ?? undefined,
      uniqueid: filters.uniqueId ?? undefined,
      name: filters.name !== '' ? filters.name.trim() : undefined,
      parent_orgs: filters.parent_orgs.length > 0 ? filters.parent_orgs.map((item) => item.id) : undefined,
      missing_value: filters.missing_value != null ? filters.missing_value.value : undefined,
      country: filters.country,
      type: filters.type !== 0 ? filters.type : undefined
    }
    const axiosConfig = {
      withCredentials: true,
      params,
      responseType: 'blob' as 'blob',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    }
    http.get('api/v1/organizations/csvs/', axiosConfig)
      .then((response) => {
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.download = `${fileName}.csv`
        link.click()
        window.URL.revokeObjectURL(url)
        setLoading(false)
        setShowSuccessExportAlert(true)
        setShowUnsuccessExportAlert(false)
      })
      .catch((error) => {
        setShowSuccessExportAlert(false)
        setShowUnsuccessExportAlert(true)
        setLoading(false)
        console.error('Error creating org:', error)
      })
  }

  const handleOncloseChange = (queryName: string): void => {
    if (queryName === '') return
    filters = JSON.parse(queryName)
    const parsedQuery = JSON.parse(queryName)
    const newFilter: OrgFilters = {
      id: filters.id ?? undefined,
      uniqueId: filters.uniqueId ?? '',
      name: parsedQuery.name ?? '',
      type: parsedQuery.type ?? 0,
      parent_orgs: parsedQuery.parent_orgs ?? [],
      missing_value: parsedQuery.missing_value ?? null,
      country: parsedQuery.country ?? null,
      country_name: parsedQuery.country_name ?? '',
      page: parsedQuery.page ?? 0,
      order_field: parsedQuery.order_field ?? '',
      order_direction: parsedQuery.order_direction ?? 'ASC'
    }

    updateFilters(newFilter)
    updateFilters(filters)
  }

  const handlePostClick = function (): any {
    const querySavedRequest = {
      name: queryName,
      is_private: isChecked,
      query: JSON.stringify(filters),
      module: {
        id: ProjectModule.getId(sessionData.module_name)
      }
    }
    http.post('/api/v1/queries-saved/', querySavedRequest, { withCredentials: true })
      .then((response) => {
        if (response.status !== 201) {
          setShowSuccessAlert(false)
          setShowUnsuccessAlert(true)
          return
        }
        setShowSuccessAlert(true)
        setShowUnsuccessAlert(false)
      })
      .catch((error) => {
        setShowSuccessAlert(false)
        setShowUnsuccessAlert(true)
        console.error('Error creating org:', error)
      })
  }

  const iconSaveButtonProps = {
    color: 'primary',
    size: 'large',
    children: <SaveIcon/>
  }

  const iconExportButtonProps = {
    color: 'primary',
    size: 'large',
    children: <FileDownloadIcon/>
  }

  const handlerClean = (): void => { cleanFilters() }

  return (
    <>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, mt: 3 }}>
        <Card>
          {showSuccessAlert && (
            <Snackbar open={showSuccessAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
              <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                This query {queryName} has been successfully saved!
              </Alert>
            </Snackbar>
          )}
          {showUnsuccessAlert && (
            <Snackbar open={showUnsuccessAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
              <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
                Failed to save the query!
              </Alert>
            </Snackbar>
          )}
            {showSuccessExportAlert && (
            <Snackbar open={showSuccessExportAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
              <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                CSV file was generation successfully!
              </Alert>
            </Snackbar>
            )}
          {showUnsuccessExportAlert && (
            <Snackbar open={showUnsuccessExportAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
              <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
                Failed to generation the  CSV file!
              </Alert>
            </Snackbar>
          )}
        </Card>
        <Card sx={{ flex: 1, p: 1.5, borderRadius: 4 }}>
          <CardContent>
            <Grid container spacing={2}>
            <Grid item xs={1}>
                <FormControl fullWidth={true} variant="outlined">
                  <TextField
                      label="ID"
                      type="number"
                      value={filters.id}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleInputChange(e, 'id') } }
                      variant="outlined" />
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <FormControl fullWidth={true} variant="outlined">
                  <TextField
                      label="Unique ID"
                      value={filters.uniqueId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleInputChange(e, 'uniqueId') } }
                      variant="outlined" />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl variant="outlined" fullWidth={true}>
                  <InputLabel>Type</InputLabel>
                  <OrganizationsTypeSelect updateFilters={updateFilters} filters={filters} disabled={false} />
                </FormControl>
              </Grid>
              <Grid item xs={5}>
                <FormControl fullWidth={true} variant="outlined">
                  <TextField
                    label="Name"
                    value={filters.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleInputChange(e, 'name') } }
                    variant="outlined" />
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <FormControl variant="outlined" fullWidth={true}>
                  <InputLabel>Missing Value</InputLabel>
                  <MissingValueSelectComponent missingValueFilters={missingValueFilters} missingValue={filters.missing_value} handleChange={handleMissingValueChange} />
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <FormControl variant="outlined" fullWidth={true}>
                  <CountrySelector handleCountryChange={handleCountryChange} filters={filters} />
                </FormControl>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth={true} variant="outlined">
                  <ParentOrgSelect filters={filters}
                    onChange={(o) => { handleParentOrgChange(o) } } />
                </FormControl>
              </Grid>
              <Grid item xs={9} />
              <Grid item xs={1}>
                <ButtonWithIcon fullWidth={true} variant="contained" size='large' nameButton='Search' icon={<FilterAltIcon />} onClick={handleFilterClick}></ButtonWithIcon>
              </Grid>
              <Grid item xs={1}>
                <ButtonWithIcon fullWidth={true} variant="contained" size='large' nameButton='Clean' icon={<CleaningServicesIcon />} onClick={handlerClean}></ButtonWithIcon>
              </Grid>
              <Grid item xs={1}>
                <Grid container justifyContent="space-between" spacing={1}>
                  <Grid item xs={3}>
                    <FormDialog isShowCheckBox={true} onCheckboxChange={handleFormCheckboxChange} onButtonClick={handlePostClick} title='Saving Search (Filters)' contentText='What name would you like to save the search as?' nameLabelField='Search Name' tooltip='Save This Search' iconButtonProps={iconSaveButtonProps} />
                  </Grid>
                  <Grid item xs={3}>
                    <SimpleDialogQuery onClose={handleOncloseChange} module={module}></SimpleDialogQuery>
                  </Grid>
                  <Grid item xs={3}>
                    <FormDialog isShowCheckBox={false} onButtonClick={handleFormExportChange} title='Choose a name for you file' contentText='Name of CSV file' nameLabelField='' tooltip='Export' iconButtonProps={iconExportButtonProps} onCheckboxChange={handleExportChange} />
                  </Grid>
                  </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
    </Box>
    {showAlertCountry &&
    <Stack sx={{ width: '100%' }} spacing={2}>
      <Alert variant="filled" severity="warning">
        You need to select a country to export the CSV file!
      </Alert>
    </Stack>}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 2, mt: 3 }}>
  {loading && <CircularProgress size={60} />}
  </Box>
    </>
  )
}
